import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo, PAGINATED_LIST_STATE_KEY } from '@theme/scroll-container';

/** ROTAS69: cards appended per task when a page is loaded in. */
const APPEND_BATCH = 6;

/**
 * ROTAS69: resolves once the next frame has been painted — or after 100 ms where frames do not
 * come (a background tab), so the caller never hangs.
 * @returns {Promise<void>}
 */
const afterNextPaint = () =>
  new Promise((resolve) => {
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      setTimeout(resolve, 0);
    };
    requestAnimationFrame(go);
    setTimeout(go, 100);
  });

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /**
   * ROTAS69: the cards of a fetched page, parsed in idle time right after the fetch. Parsing the
   * page's HTML and inserting its 24 cards used to happen inside the "show more" tap itself:
   * 864 ms from tap to the next frame on a 4x-slowed phone (main, 01.11.2026) — the worst
   * interaction on the site by far, and in Clarity the top dead click of the collection pages,
   * because the spinner could not paint before all of that was done.
   * @type {Map<number, Element[]>}
   */
  #parsed = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {MutationObserver | undefined} */
  #gridObserver;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #updateTimer;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /**
   * ROTAS69: writes the position for a reload once scrolling has rested (see #onScroll).
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #saveTimer;

  /**
   * ROTAS69: checks the page in the address while scrolling (see #onScroll).
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pageTimer;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // ROTAS69: what is on screen goes into the history entry, so a reload or Back returns to it
    // (#saveLoadedState). pagehide catches a reload made before scrolling has rested.
    document.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('pagehide', this.#saveLoadedState);

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    this.#gridObserver?.disconnect();
    clearTimeout(this.#updateTimer);
    document.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('pagehide', this.#saveLoadedState);
    clearTimeout(this.#saveTimer);
    clearTimeout(this.#pageTimer);
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  /**
   * Wires the two load-more controls and shows the ones that lead somewhere. Loading is driven by
   * the shopper, never by scroll position — the IntersectionObserver below stays for lists that
   * still render bare sentinels instead of the r69 control.
   */
  #setupLoadMoreButtons() {
    const { viewMorePrevious, viewMoreNext } = this.refs;
    /** @type {HTMLElement | null | undefined} */
    const nextButton = viewMoreNext?.querySelector('[data-r69lm-next]');
    /** @type {HTMLElement | null | undefined} */
    const previousButton = viewMorePrevious?.querySelector('[data-r69lm-previous]');

    if (!nextButton && !previousButton) return false;

    if (nextButton && !nextButton.dataset.bound) {
      nextButton.dataset.bound = '1';
      nextButton.addEventListener('click', (event) => this.#onLoadMoreClick(event, nextButton, 'next'));
    }

    if (previousButton && !previousButton.dataset.bound) {
      previousButton.dataset.bound = '1';
      previousButton.addEventListener('click', (event) => this.#onLoadMoreClick(event, previousButton, 'previous'));
    }

    // The control follows the GRID, not the render promise. Whatever puts cards in — a click, the
    // scroll observer, a filter update — the count and the buttons settle from the DOM afterwards.
    // Awaiting the render instead would hang wherever its internal yield does: it waits on
    // requestAnimationFrame, which never fires in a background tab, and the button would stay
    // spinning over a grid that already grew.
    const { grid } = this.refs;
    if (grid && !this.#gridObserver) {
      this.#gridObserver = new MutationObserver(() => {
        clearTimeout(this.#updateTimer);
        this.#updateTimer = setTimeout(() => this.#updateLoadMore(), 60);
      });
      this.#gridObserver.observe(grid, { childList: true });
    }

    this.#updateLoadMore();
    return true;
  }

  /**
   * Keeps the count, the progress bar and the visibility of both buttons in step with what is
   * actually on screen. The count states what is LOADED, so reloading on ?page=3 reads 48 of 480
   * rather than 144 — the earlier pages are not there until the shopper asks for them.
   */
  #updateLoadMore() {
    const { grid, viewMoreNext, viewMorePrevious } = this.refs;
    if (!grid) return;

    const loaded = grid.querySelectorAll(':scope > [ref="cards[]"]').length;
    const countElement = viewMoreNext?.querySelector('[data-r69lm-count]');
    const total = Number(countElement?.dataset.total ?? grid.dataset.totalItems ?? 0);

    if (countElement && total > 0) {
      countElement.textContent = (countElement.dataset.template ?? '%loaded% / %total%')
        .replace('%loaded%', String(loaded))
        .replace('%total%', String(total));
    }

    const fill = viewMoreNext?.querySelector('[data-r69lm-fill]');
    if (fill && total > 0) fill.style.width = `${Math.min(100, (loaded / total) * 100)}%`;

    // Read the edge pages from the DOM rather than from refs: refs are refreshed by a
    // MutationObserver, so right after a batch is appended they can still describe the old grid.
    const cards = grid.querySelectorAll(':scope > [ref="cards[]"]');
    const lastPage = Number(grid.dataset.lastPage ?? 0);
    const firstCardPage = Number(/** @type {HTMLElement} */ (cards[0])?.dataset.page ?? 0);
    const lastCardPage = Number(/** @type {HTMLElement} */ (cards[cards.length - 1])?.dataset.page ?? 0);

    const nextWrap = viewMoreNext?.querySelector('[data-r69lm-next-wrap]');
    if (nextWrap) nextWrap.hidden = !(lastCardPage > 0 && lastCardPage < lastPage);

    const previousWrap = viewMorePrevious?.querySelector('[data-r69lm-previous-wrap]');
    if (previousWrap) previousWrap.hidden = !(firstCardPage > 1);

    const nextControl = viewMoreNext?.querySelector('[data-r69lm-next]');
    const previousControl = viewMorePrevious?.querySelector('[data-r69lm-previous]');

    // The links follow the pages actually on screen: once page 2 has been put in place, "show more"
    // points at page 3 — what a new tab, a crawler or a shopper without this script then opens.
    if (lastCardPage > 0 && lastCardPage < lastPage) this.#pointAt(nextControl, lastCardPage + 1);
    if (firstCardPage > 1) this.#pointAt(previousControl, firstCardPage - 1);

    for (const control of [nextControl, previousControl]) {
      if (control instanceof HTMLElement) this.#setControlBusy(control, false);
    }
  }

  /**
   * A click on "show more" or "show previous". The control is a real link to that page when the
   * list knows its address (snippets/r69-load-more.liquid), a button otherwise. A plain click puts
   * the page in place here instead of opening it; a click with a modifier key is left to the
   * browser, so "open in a new tab" works on the link. If the page cannot be put in place, the link
   * is followed instead, and a button stops spinning.
   * @param {MouseEvent} event
   * @param {HTMLElement} control
   * @param {'next' | 'previous'} direction
   */
  #onLoadMoreClick(event, control, direction) {
    const link = control instanceof HTMLAnchorElement ? control : null;
    if (link && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;

    event.preventDefault();
    if (control.classList.contains('is-loading')) return;
    this.#setControlBusy(control, true);

    const render = direction === 'next' ? this.#renderNextPage() : this.#renderPreviousPage();
    const fallBack = () => {
      if (link) window.location.assign(link.href);
      else this.#setControlBusy(control, false);
    };
    render.then((placed) => {
      if (!placed) fallBack();
    }, fallBack);
  }

  /**
   * Shows a control as busy while a page is being put in place: a button is disabled, a link
   * carries aria-disabled (a link has no disabled state of its own).
   * @param {HTMLElement} control
   * @param {boolean} busy
   */
  #setControlBusy(control, busy) {
    control.classList.toggle('is-loading', busy);
    if (control instanceof HTMLButtonElement) {
      control.disabled = busy;
    } else if (busy) {
      control.setAttribute('aria-disabled', 'true');
    } else {
      control.removeAttribute('aria-disabled');
    }
  }

  /**
   * Points a link control at another page of the same list, keeping its filters and sort order.
   * @param {Element | null | undefined} control
   * @param {number} page
   */
  #pointAt(control, page) {
    if (!(control instanceof HTMLAnchorElement) || page < 1) return;
    const url = new URL(control.href);
    url.searchParams.set('page', String(page));
    control.setAttribute('href', `${url.pathname}${url.search}`);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Buttons take over entirely when the list renders them.
    if (this.#setupLoadMoreButtons()) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
    requestIdleCallback(() => {
      if (this.pages.get(pageNumber) === pageContent && !this.#parsed.has(pageNumber)) {
        const items = this.#parseGridForPage(pageNumber);
        if (items) this.#parsed.set(pageNumber, items);
      }
    });
  }

  /**
   * @returns {Promise<boolean>} whether the next page was put in place
   */
  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return false;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return false;

    // Let the tap paint first (the spinner on the button), then do the work.
    await afterNextPaint();

    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return false;
    }

    // A few cards per task: each card upgrades several components, and 24 at once were one long task.
    const items = [...nextPageItemElements];
    for (let i = 0; i < items.length; i += APPEND_BATCH) {
      if (i > 0) await yieldToMainThread();
      grid.append(...items.slice(i, i + APPEND_BATCH));
    }

    // The helper is only built when the list has a [ref="cardGallery"] template card — product
    // grids do, an article grid does not — so this has to be optional or the first "load more"
    // on any other kind of list throws before the count and buttons can settle.
    this.#aspectRatioHelper?.processNewElements();

    await yieldToMainThread();

    this.#saveLoadedState();

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });

    return true;
  }

  /**
   * @returns {Promise<boolean>} whether the previous page was put in place
   */
  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return false;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return false;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return false;
    }

    this.#prependKeepingView(grid, previousPageItemElements);

    await yieldToMainThread();

    this.#saveLoadedState();

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });

    return true;
  }

  /**
   * ROTAS69: prepends a page and keeps what is on screen where it was. The reference is the first
   * CARD: in product grids the grid's first element child is the r69-product-grid-inline.js
   * <script>, which has no box, so measuring it (as this code used to) compensated nothing and the
   * view only held where the browser's own scroll anchoring stepped in.
   * @param {HTMLElement} grid
   * @param {Element[]} items
   */
  #prependKeepingView(grid, items) {
    const reference = grid.querySelector(':scope > [ref="cards[]"]');
    const scrollTop = getScrollTop();
    const before = reference ? reference.getBoundingClientRect().top + scrollTop : 0;

    grid.prepend(...items);
    this.#aspectRatioHelper?.processNewElements();

    if (reference) {
      const after = reference.getBoundingClientRect().top + getScrollTop();
      scrollTo({ top: scrollTop + after - before, behavior: 'instant' });
    }
  }

  /**
   * ROTAS69: two cadences. The page in the address is checked every 150 ms while the list scrolls,
   * so ?page= follows the screen without waiting for the scroll to stop (a trailing timer alone
   * changed it only after a pause). The position for a reload is written once scrolling has rested
   * for 200 ms — it only has to be right when the shopper leaves, and browsers throttle frequent
   * history.replaceState calls.
   */
  #onScroll = () => {
    if (!this.#pageTimer) this.#pageTimer = setTimeout(this.#syncPageInView, 150);
    clearTimeout(this.#saveTimer);
    this.#saveTimer = setTimeout(this.#saveLoadedState, 200);
  };

  /**
   * ROTAS69: the cards to track, or null when there is nothing to track — a single server-rendered
   * page and no record yet restores like any other page (assets/scroll-container.js).
   * @returns {NodeListOf<HTMLElement> | null}
   */
  #trackedCards() {
    const { grid } = this.refs;
    if (!grid || !this.isConnected) return null;

    const cards = /** @type {NodeListOf<HTMLElement>} */ (grid.querySelectorAll(':scope > [ref="cards[]"]'));
    if (!cards.length) return null;

    const state = history.state && typeof history.state === 'object' ? history.state : {};
    const first = Number(cards[0].dataset.page);
    const last = Number(cards[cards.length - 1].dataset.page);
    if (first === last && !state[PAGINATED_LIST_STATE_KEY]) return null;

    return cards;
  }

  /**
   * ROTAS69: the first card whose bottom is below the top of the viewport, or the last card when the
   * whole grid is above it. A binary search (about 8 measurements for 132 cards): the cards are in
   * visual order — their CSS `order` rises with the DOM — so their bottoms never decrease.
   * @param {NodeListOf<HTMLElement>} cards
   * @returns {HTMLElement}
   */
  #topCard(cards) {
    let low = 0;
    let high = cards.length - 1;
    while (low < high) {
      const middle = (low + high) >> 1;
      if (/** @type {HTMLElement} */ (cards[middle]).getBoundingClientRect().bottom > 0) high = middle;
      else low = middle + 1;
    }
    return /** @type {HTMLElement} */ (cards[low]);
  }

  /** ROTAS69: rewrites the address only when the page on screen is not the one in it. */
  #syncPageInView = () => {
    this.#pageTimer = undefined;
    const cards = this.#trackedCards();
    if (!cards) return;

    const pageInView = Number(this.#topCard(cards).dataset.page);
    const pageInUrl = Number(new URL(window.location.href).searchParams.get('page') || 1);
    if (pageInView !== pageInUrl) this.#saveLoadedState();
  };

  /**
   * ROTAS69: writes what the shopper sees into the current history entry, under
   * PAGINATED_LIST_STATE_KEY: `s` — this list's section, `a` — the anchor (the top card on screen by
   * its id, or page:index for cards without one; once the whole grid is above the screen, the id of
   * the page section at its top), `o` — the anchor's distance from the top of the viewport.
   * After a reload or Back the server renders only the page in the address, and
   * snippets/r69-list-restore.liquid puts the anchor back at that distance before the first frame.
   * The other loaded pages are not loaded again: "show previous" / "show more" bring them back.
   *
   * The address follows the page on screen through replaceState: no history entry per loaded page,
   * so Back leaves the list instead of stepping through ?page= addresses. No page_view comes of it —
   * GA4's history-change tracking is off and GTM has no History Change trigger (checked with the
   * hits intercepted, 01.11.2026); every ?page=N view in GA4 is a real load.
   */
  #saveLoadedState = () => {
    const cards = this.#trackedCards();
    if (!cards) return;

    const state = history.state && typeof history.state === 'object' ? history.state : {};
    const card = this.#topCard(cards);
    const section =
      card.getBoundingClientRect().bottom > 0
        ? null
        : [...document.querySelectorAll('#MainContent > .shopify-section[id], #MainContent ~ footer .shopify-section[id]')].find(
            (element) => element.getBoundingClientRect().bottom > 0
          );
    const anchor = /** @type {HTMLElement} */ (section ?? card);

    const record = {
      s: this.sectionId,
      a: section ? section.id : this.#anchorKey(card),
      o: Math.round(this.#layoutTop(anchor)),
    };

    const url = new URL(window.location.href);
    const pageInView = Number(card.dataset.page);
    if (pageInView > 1) url.searchParams.set('page', String(pageInView));
    else url.searchParams.delete('page');

    try {
      history.replaceState({ ...state, [PAGINATED_LIST_STATE_KEY]: record }, '', url);
    } catch (_) {
      return;
    }
  };

  /**
   * ROTAS69: the element's top in the viewport without its own transform. A card inserted into a
   * results list slides in from translateY(10px) (the @starting-style rule on
   * results-list[initialized] in base.css); measured mid-slide, it put a saved anchor off by up to
   * 10 px.
   * @param {HTMLElement} element
   * @returns {number}
   */
  #layoutTop(element) {
    const { transform } = getComputedStyle(element);
    const shift = transform && transform !== 'none' ? new DOMMatrixReadOnly(transform).m42 : 0;
    return element.getBoundingClientRect().top - shift;
  }

  /**
   * ROTAS69: product cards carry an id (section + product); article cards in the blog do not, so
   * those are keyed by their page and position on it.
   * @param {HTMLElement} card
   * @returns {string}
   */
  #anchorKey(card) {
    if (card.id) return card.id;

    const page = card.dataset.page ?? '';
    const samePage = [...(this.refs.grid?.querySelectorAll(`:scope > [ref="cards[]"][data-page="${page}"]`) ?? [])];
    return `${page}:${samePage.indexOf(card)}`;
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const isPrevious = type === 'previous';
    // ROTAS69: the edge cards are read from the DOM, not from refs. Refs follow a MutationObserver
    // that starts in idle time, so right after cards are inserted they can still describe the old
    // grid — and the "next" page would be one that is already on screen.
    const cards = /** @type {NodeListOf<HTMLElement> | undefined} */ (
      this.refs.grid?.querySelectorAll(':scope > [ref="cards[]"]')
    );

    if (!cards?.length) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {Element[] | undefined}
   */
  #getGridForPage(page) {
    const parsed = this.#parsed.get(page);
    if (parsed) {
      // The nodes move into the grid; using the page again would need a fresh parse.
      this.#parsed.delete(page);
      this.pages.delete(page);
      return parsed;
    }

    // ROTAS69: the page's HTML goes once its nodes are taken, so the idle pre-parse scheduled in
    // #fetchSpecificPage does not parse a page that is already in the grid.
    const items = this.#parseGridForPage(page);
    this.pages.delete(page);
    return items;
  }

  /**
   * @param {number} page
   * @returns {Element[] | undefined}
   */
  #parseGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    // Take the page's grid banners along with its cards — copying only the cards is why an
    // appended page used to arrive without any banner. Each page's banners come in their own
    // wrapper carrying the CSS that places them, so the two pages cannot interfere.
    return [...gridElement.querySelectorAll(':scope > [ref="cards[]"], :scope > .pg-banners')];
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.pages.clear();
    this.#parsed.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
